// Helper function to generate dynamic routes based on role
const generateRoleBasedRoutes = () => {
  const getRoleBasedPath = (basePath) => {
    return `/:rolePrefix(${basePath})`;
  };

  return [
    {
      path: "/login",
      name: "login",
      component: () => import("@/views/authentication/Login.vue"),
      meta: {
        title: "Login",
        redirectIfLoggedIn: true,
      },
    },
    {
      path: "/forgot-password",
      name: "forgot-password",
      component: () => import("@/views/authentication/ForgotPassword.vue"),
      meta: {
        title: "Forgot Password",
        redirectIfLoggedIn: true,
      },
    },
    {
      path: "/reset-password",
      name: "reset-password",
      component: () => import("@/views/authentication/ResetPassword.vue"),
      meta: {
        title: "Reset Password",
      },
    },
    {
      path: "/logout",
      name: "logout",
      meta: {
        title: "Logout",
        authRequired: true,
      },
      component: () => import("@/views/authentication/Logout.vue"),
    },

    // Dashboard - akan dihandle oleh component yang dinamis
    {
      path: "/",
      name: "dashboard",
      meta: {
        title: "Dashboard",
        authRequired: true,
      },
      component: () => import("@/views/dashboard/DashboardWrapper.vue"),
    },

    // Role management (admin only)
    // setting account
    {
      path: getRoleBasedPath("admin|staff|doctor") + "/setting-account",
      name: "settingAccount",
      meta: {
        title: "Setting Account",
        authRequired: true,
      },
      component: () => import("@/views/admin/setting-account/SettingPage.vue"),
    },

    {
      path: getRoleBasedPath("admin") + "/roles",
      name: "rolePage",
      meta: {
        title: "Role",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/role-management/RolePage.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/roles/form/:id?",
      name: "roleFormPage",
      meta: {
        title: "Role Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/role-management/RoleForm.vue"),
    },

    // Admin management (admin only)
    {
      path: getRoleBasedPath("admin") + "/admins",
      name: "adminPage",
      meta: {
        title: "Admin",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/admin-management/AdminPage.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/admins/form/:id?",
      name: "adminFormPage",
      meta: {
        title: "Admin Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/admin-management/AdminForm.vue"),
    },

    // Customers (admin & staff)
    {
      path: getRoleBasedPath("admin|staff") + "/customers",
      name: "customersPage",
      meta: {
        title: "Customer",
        authRequired: true,
        allowedRoles: [1, 2], // Super Admin & Staff
      },
      component: () => import("@/views/admin/customer/CustomerPage.vue"),
    },
    {
      path: getRoleBasedPath("admin|staff") + "/customers/view/:id?",
      name: "CustomerView",
      meta: {
        title: "Customer View",
        authRequired: true,
        allowedRoles: [1, 2], // Super Admin & Staff
      },
      component: () => import("@/views/admin/customer/CustomerView.vue"),
    },
    {
      path:
        getRoleBasedPath("admin|staff") +
        "/customers/:customerId/order-history",
      name: "customerOrderPage",
      meta: {
        title: "Order History",
        authRequired: true,
        allowedRoles: [1, 2], // Super Admin & Staff
      },
      component: () => import("@/views/admin/order-history/OrderPage.vue"),
      props: true,
    },

    {
      path: getRoleBasedPath("admin|staff") + "/customers/:customerId/point",
      name: "PointView",
      meta: {
        title: "Point History",
        authRequired: true,
        allowedRoles: [1, 2], // Super Admin & Staff
      },
      component: () => import("@/views/admin/poin-history/PoinHistoryPage.vue"),
      props: true,
    },

    // Product management (admin only)
    {
      path: getRoleBasedPath("admin") + "/products",
      name: "productPage",
      meta: {
        title: "Product",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/product/ProductPage.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/products/form/:id?",
      name: "productFormPage",
      meta: {
        title: "Product Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/product/ProductForm.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/products/view/:id?",
      name: "productReviewPage",
      meta: {
        title: "Product Review",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/product/ProductReview.vue"),
    },

    // FAQ management (admin only)
    {
      path: getRoleBasedPath("admin") + "/faqs",
      name: "faqPage",
      meta: {
        title: "FAQ",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/faq-management/FaqPage.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/faqs/faqGroup/form/:id?",
      name: "faqGroupFormPage",
      meta: {
        title: "FAQ Group Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/faq-management/FaqGroupForm.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/faqs/faq/form/:id?",
      name: "faqFormPage",
      meta: {
        title: "FAQ Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/faq-management/FaqForm.vue"),
    },

    // Doctor management (admin only)
    {
      path: getRoleBasedPath("admin") + "/doctors",
      name: "doctorPage",
      meta: {
        title: "Doctor",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/doctor-management/DoctorPage.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/doctors/form/:id?",
      name: "doctorFormPage",
      meta: {
        title: "Doctor Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/doctor-management/DoctorForm.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/doctors/view/:id?",
      name: "doctorViewPage",
      meta: {
        title: "Doctor View",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/doctor-management/DoctorView.vue"),
    },

    // Staff clinic management (admin only)
    {
      path: getRoleBasedPath("admin") + "/staff-clinic",
      name: "staffclinicPage",
      meta: {
        title: "Staff Clinic",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/staff-clinic/StaffclinicPage.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/staff-clinic/form/:id?",
      name: "staffclinicFormPage",
      meta: {
        title: "Staff Clinic Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/staff-clinic/StaffclinicForm.vue"),
    },

    // Promo management (admin only)
    {
      path: getRoleBasedPath("admin") + "/promos",
      name: "promoPage",
      meta: {
        title: "Promo",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/promo/PromoPage.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/promos/form/:id?",
      name: "promoFormPage",
      meta: {
        title: "Promo Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/promo/PromoForm.vue"),
    },

    // Treatment management (admin only)
    {
      path: getRoleBasedPath("admin") + "/treatments",
      name: "treatmentPage",
      meta: {
        title: "Treatment",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/treatment/TreatmentPage.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/treatments/form/:id?",
      name: "treatmentFormPage",
      meta: {
        title: "Treatment Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/treatment/TreatmentForm.vue"),
    },

    // Loyalty program management (admin only)
    {
      path: getRoleBasedPath("admin") + "/loyalty-programs",
      name: "loyaltyprogramPage",
      meta: {
        title: "Loyalty Program",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () =>
        import("@/views/admin/loyalty-program/LoyaltyProgramPage.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/loyalty-programs/form/:id?",
      name: "loyaltyprogramFormPage",
      meta: {
        title: "Loyalty Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () =>
        import("@/views/admin/loyalty-program/LoyaltyProgramForm.vue"),
    },

    // Questioner management (admin only)
    {
      path: getRoleBasedPath("admin") + "/questioners",
      name: "questionerPage",
      meta: {
        title: "Questioner",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/questioner/QuestionerPage.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/questioners/form/:id?",
      name: "questionerFormPage",
      meta: {
        title: "Questioner Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/questioner/QuestionerForm.vue"),
    },

    // Questioner group management (admin only)
    {
      path: getRoleBasedPath("admin") + "/questioner-groups",
      name: "questionergroupPage",
      meta: {
        title: "Questioner Group",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () =>
        import("@/views/admin/questioner-group/QuestionerGroupPage.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/questioner-groups/form/:id?",
      name: "questionerGroupFormPage",
      meta: {
        title: "Questioner Group Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () =>
        import("@/views/admin/questioner-group/QuestionerGroupForm.vue"),
    },

    // Job management (admin only)
    {
      path: getRoleBasedPath("admin") + "/jobs",
      name: "jobPage",
      meta: {
        title: "Job",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/job-management/JobPage.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/jobs/form/:id?",
      name: "jobFormPage",
      meta: {
        title: "Job Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/job-management/JobForm.vue"),
    },

    // Service center management (admin only)
    {
      path: getRoleBasedPath("admin") + "/service-centers",
      name: "serviceCenterPage",
      meta: {
        title: "Service Center",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () =>
        import("@/views/admin/service-center/ServiceCenterPage.vue"),
    },

    // Order management (admin & staff)
    {
      path: getRoleBasedPath("admin|staff") + "/orders",
      name: "adminOrderPage",
      meta: {
        title: "Order History",
        authRequired: true,
        allowedRoles: [1, 2], // Super Admin & Staff
      },
      component: () => import("@/views/admin/order-history/OrderPage.vue"),
    },
    {
      path: getRoleBasedPath("admin|staff") + "/orders/view/:id",
      name: "OrderView",
      meta: {
        title: "Order View",
        authRequired: true,
        allowedRoles: [1, 2], // Super Admin & Staff
      },
      component: () => import("@/views/admin/order-history/OrderView.vue"),
      props: (route) => ({
        orderId: route.params.id,
        customerId: route.query.customerId,
      }),
    },

    // Consultation management (admin only)
    {
      path: getRoleBasedPath("admin") + "/consultations",
      name: "consultationPage",
      meta: {
        title: "Consultation",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () =>
        import("@/views/admin/consultation/ConsultationPage.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/consultations/view/:id?",
      name: "consultationView",
      meta: {
        title: "Consultation View",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () =>
        import("@/views/admin/consultation/ConsultationView.vue"),
    },
    {
      path:
        getRoleBasedPath("admin|staff") +
        "/customers/:customerId/consultation-history",
      name: "customerConsultationPage",
      meta: {
        title: "Consultation History",
        authRequired: true,
        allowedRoles: [1, 2], // Super Admin & Staff
      },
      component: () =>
        import("@/views/admin/consultation/ConsultationPage.vue"),
      props: true,
    },

    // Doctor consultation management
    {
      path: getRoleBasedPath("doctor") + "/consultations",
      name: "doctorConsultationPage",
      meta: {
        title: "My Consultations",
        authRequired: true,
        allowedRoles: [3], // Doctor only
      },
      component: () =>
        import("@/views/admin/consultation/DoctorConsultationPage.vue"),
    },
    {
      path: getRoleBasedPath("doctor") + "/consultations/view/:id?",
      name: "doctorConsultationView",
      meta: {
        title: "Consultation View",
        authRequired: true,
        allowedRoles: [3], // Doctor only
      },
      component: () =>
        import("@/views/admin/consultation/DoctorConsultationView.vue"),
    },

    // Claim reward management (admin & staff)
    {
      path: getRoleBasedPath("admin|staff") + "/claim-rewards",
      name: "claimrewardPage",
      meta: {
        title: "Claim Reward",
        authRequired: true,
        allowedRoles: [1, 2], // Super Admin & Staff
      },
      component: () => import("@/views/admin/claim-reward/ClaimRewardPage.vue"),
    },

    // Master Data (admin only)
    // Polyclinic management
    {
      path: getRoleBasedPath("admin") + "/master-data/polyclinics",
      name: "polyclinicPage",
      meta: {
        title: "Polyclinic",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/polyclinic/PolyclinicPage.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/master-data/polyclinics/form/:id?",
      name: "polyclinicFormPage",
      meta: {
        title: "Polyclinic Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/polyclinic/PolyclinicForm.vue"),
    },

    // Specialization management
    {
      path: getRoleBasedPath("admin") + "/master-data/specializations",
      name: "specializationPage",
      meta: {
        title: "Specialization",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () =>
        import("@/views/admin/specialization/SpecializationPage.vue"),
    },
    {
      path:
        getRoleBasedPath("admin") + "/master-data/specializations/form/:id?",
      name: "specializationFormPage",
      meta: {
        title: "Specialization Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () =>
        import("@/views/admin/specialization/SpecializationForm.vue"),
    },

    // Brand management
    {
      path: getRoleBasedPath("admin") + "/master-data/brands",
      name: "brandPage",
      meta: {
        title: "Brand",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/brand/BrandPage.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/master-data/brands/form/:id?",
      name: "brandFormPage",
      meta: {
        title: "Brand Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/brand/BrandForm.vue"),
    },

    // Product category management
    {
      path: getRoleBasedPath("admin") + "/master-data/product-categories",
      name: "productcategoryPage",
      meta: {
        title: "Product Category",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () =>
        import("@/views/admin/product-category/ProductCategoryPage.vue"),
    },
    {
      path:
        getRoleBasedPath("admin") + "/master-data/product-categories/form/:id?",
      name: "ProductCategoryForm",
      meta: {
        title: "Product Category Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () =>
        import("@/views/admin/product-category/ProductCategoryForm.vue"),
    },

    // Spot management
    {
      path: getRoleBasedPath("admin") + "/master-data/spots",
      name: "spotPage",
      meta: {
        title: "Spot",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/spot/SpotPage.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/master-data/spots/form/:id?",
      name: "SpotFormPage",
      meta: {
        title: "Spot Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/spot/SpotForm.vue"),
    },

    // Department management
    {
      path: getRoleBasedPath("admin") + "/master-data/departments",
      name: "departmentPage",
      meta: {
        title: "Department",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () =>
        import("@/views/admin/department-management/DepartmentPage.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/master-data/departments/form/:id?",
      name: "departmentFormPage",
      meta: {
        title: "Department Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () =>
        import("@/views/admin/department-management/DepartmentForm.vue"),
    },

    // Item reward management
    {
      path: getRoleBasedPath("admin") + "/master-data/item-rewards",
      name: "itemrewardPage",
      meta: {
        title: "Item Reward",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/item-reward/ItemrewardPage.vue"),
    },
    {
      path: getRoleBasedPath("admin") + "/master-data/item-rewards/form/:id?",
      name: "itemrewardFormPage",
      meta: {
        title: "Item Reward Form",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/item-reward/ItemrewardForm.vue"),
    },

    // Reports (admin only)
    // Transaction report
    {
      path: getRoleBasedPath("admin") + "/reports/transactions",
      name: "reportransactionPage",
      meta: {
        title: "Report Transaction",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/report/ReportTransaction.vue"),
    },

    // Redeem point report
    {
      path: getRoleBasedPath("admin") + "/reports/redeem-points",
      name: "reportredeemPage",
      meta: {
        title: "Report Redeem Point",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/report/ReportRedeemPoint.vue"),
    },

    // Sales report
    {
      path: getRoleBasedPath("admin") + "/reports/sales",
      name: "reportpenjualanPage",
      meta: {
        title: "Report Penjualan",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/report/ReportPenjualan.vue"),
    },

    // Consultation report
    {
      path: getRoleBasedPath("admin") + "/reports/consultations",
      name: "reportconsultationPage",
      meta: {
        title: "Report Consultation",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () => import("@/views/admin/report/ReportConsultation.vue"),
    },

    // Content management (admin only)
    // Urutan section
    {
      path:
        getRoleBasedPath("admin") +
        "/content-management/setting-homepage/urutan-section",
      name: "urutanSectionPage",
      meta: {
        title: "Urutan Section",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () =>
        import(
          "@/views/admin/content-management/setting-homepage/UrutanSection.vue"
        ),
    },

    // Content Home Bahasa
    {
      path:
        getRoleBasedPath("admin") +
        "/content-management/setting-homepage/content-bahasa",
      name: "contentHomeBahasa",
      meta: {
        title: "Content Home Bahasa",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      props: { language: "id" },
      component: () =>
        import(
          "@/views/admin/content-management/setting-homepage/ContentHomeBahasa.vue"
        ),
    },

    // Content Home English
    {
      path:
        getRoleBasedPath("admin") +
        "/content-management/setting-homepage/content-english",
      name: "contentHomeEnglish",
      meta: {
        title: "Content Home English",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      props: { language: "en" },
      component: () =>
        import(
          "@/views/admin/content-management/setting-homepage/ContentHomeBahasa.vue"
        ),
    },

    // Setting About Bahasa
    {
      path:
        getRoleBasedPath("admin") + "/content-management/setting-about-bahasa",
      name: "SettingAboutBahasa",
      meta: {
        title: "Setting About Bahasa",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      props: { language: "id" },
      component: () =>
        import(
          "@/views/admin/content-management/setting-about/ContentAbout.vue"
        ),
    },

    // Setting About English
    {
      path:
        getRoleBasedPath("admin") + "/content-management/setting-about-english",
      name: "SettingAboutEnglish",
      meta: {
        title: "Setting About English",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      props: { language: "en" },
      component: () =>
        import(
          "@/views/admin/content-management/setting-about/ContentAbout.vue"
        ),
    },

    // Setting Lifestyle
    {
      path: getRoleBasedPath("admin") + "/content-management/setting-lifestyle",
      name: "SettingLifestyle",
      meta: {
        title: "Setting Lifestyle",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () =>
        import(
          "@/views/admin/content-management/setting-lifestyle/SettingLifestylePage.vue"
        ),
    },
    {
      path:
        getRoleBasedPath("admin") +
        "/content-management/setting-lifestyle/form/:id?",
      name: "FormLifestyle",
      meta: {
        title: "Form Lifestyle",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () =>
        import(
          "@/views/admin/content-management/setting-lifestyle/SettingLifestyleForm.vue"
        ),
    },

    // Setting Contact Bahasa
    {
      path:
        getRoleBasedPath("admin") +
        "/content-management/setting-contact-bahasa",
      name: "SettingContactBahasa",
      meta: {
        title: "Setting Contact Bahasa",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      props: { language: "id" },
      component: () =>
        import(
          "@/views/admin/content-management/setting-contact/ContactSetting.vue"
        ),
    },

    // Setting Contact English
    {
      path:
        getRoleBasedPath("admin") +
        "/content-management/setting-contact-english",
      name: "SettingContactEnglish",
      meta: {
        title: "Setting Contact English",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      props: { language: "en" },
      component: () =>
        import(
          "@/views/admin/content-management/setting-contact/ContactSetting.vue"
        ),
    },

    // Setting Privacy Bahasa
    {
      path:
        getRoleBasedPath("admin") +
        "/content-management/setting-privacy-bahasa",
      name: "SettingPrivacyBahasa",
      meta: {
        title: "Setting Privacy Bahasa",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      props: { language: "id" },
      component: () =>
        import(
          "@/views/admin/content-management/setting-privacy/PrivacySetting.vue"
        ),
    },

    // Setting Privacy English
    {
      path:
        getRoleBasedPath("admin") +
        "/content-management/setting-privacy-english",
      name: "SettingPrivacyEnglish",
      meta: {
        title: "Setting Privacy English",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      props: { language: "en" },
      component: () =>
        import(
          "@/views/admin/content-management/setting-privacy/PrivacySetting.vue"
        ),
    },

    // Setting Terms Bahasa
    {
      path:
        getRoleBasedPath("admin") + "/content-management/setting-terms-bahasa",
      name: "SettingTermsBahasa",
      meta: {
        title: "Setting Terms Bahasa",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      props: { language: "id" },
      component: () =>
        import(
          "@/views/admin/content-management/setting-terms/TermsSetting.vue"
        ),
    },

    // Setting Terms English
    {
      path:
        getRoleBasedPath("admin") + "/content-management/setting-terms-english",
      name: "SettingTermsEnglish",
      meta: {
        title: "Setting Terms English",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      props: { language: "en" },
      component: () =>
        import(
          "@/views/admin/content-management/setting-terms/TermsSetting.vue"
        ),
    },

    // Setting Other
    {
      path: getRoleBasedPath("admin") + "/content-management/setting-other",
      name: "SettingOther",
      meta: {
        title: "Setting Other",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () =>
        import(
          "@/views/admin/content-management/setting-other/OtherwebSetting.vue"
        ),
    },

    // Setting Mobile
    {
      path: getRoleBasedPath("admin") + "/content-management/setting-mobile",
      name: "SettingMobile",
      meta: {
        title: "Setting Mobile",
        authRequired: true,
        allowedRoles: [1], // Super Admin only
      },
      component: () =>
        import(
          "@/views/admin/content-management/setting-mobile/MobileSetting.vue"
        ),
    },

    // Messages (all roles)
    {
      path: getRoleBasedPath("admin|staff|doctor") + "/messages",
      name: "MessagePage",
      meta: {
        title: "Message",
        authRequired: true,
        allowedRoles: [1, 2, 3], // All roles
      },
      component: () => import("@/views/admin/chat/ChatPage.vue"),
    },
  ];
};

export default generateRoleBasedRoutes();
